"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, Loader2, Plus, X, MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { DocumentManager } from "@/components/planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Info, TrendingUp, TrendingDown } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { CardDescription } from "@/components/ui/card";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface TrdPblRatioAnalysisProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface RatioResponse {
  id: string;
  textResponse?: { html: string; plainText: string } | null;
  documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
}

interface RatioCardProps {
  ratio: {
    name: string;
    key: string;
    formula: string;
    description: string;
    isPercentage: boolean;
    isDays: boolean;
  };
  chartData: { date: string; value: number }[];
}

function RatioCard({ ratio, chartData }: RatioCardProps) {
  const latestValue = chartData[chartData.length - 1]?.value;
  const previousValue = chartData[chartData.length - 2]?.value;
  const trend =
    latestValue !== undefined && previousValue !== undefined
      ? latestValue > previousValue
        ? "up"
        : "down"
      : null;

  const formatValue = (value: number) => {
    if (ratio.isPercentage) {
      return `${(value * 100).toFixed(2)}%`;
    } else if (ratio.isDays) {
      return `${Math.round(value)} days`;
    } else {
      return value.toFixed(2);
    }
  };

  const formatTooltipValue = (value: number) => {
    if (ratio.isPercentage) {
      return `${(value * 100).toFixed(2)}%`;
    } else if (ratio.isDays) {
      return `${Math.round(value)} days`;
    } else {
      return value.toFixed(2);
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="rounded-lg border border-white/20 bg-black/90 p-3 text-white shadow-xl backdrop-blur-md">
          <p className="font-semibold text-sm">{`${ratio.name}`}</p>
          <p className="mb-1 text-xs text-white/60">{label}</p>
          <p className="text-base font-bold text-[#4da3ff]">
            {formatTooltipValue(data.value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="h-full border-white/10 bg-white/5 backdrop-blur-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{ratio.name}</CardTitle>
          {trend && (
            <Badge
              variant={trend === "up" ? "default" : "destructive"}
              className="border-white/10 text-xs"
            >
              {trend === "up" ? (
                <TrendingUp className="mr-1 h-3 w-3" />
              ) : (
                <TrendingDown className="mr-1 h-3 w-3" />
              )}
              {trend === "up" ? "Up" : "Down"}
            </Badge>
          )}
        </div>
        <CardDescription className="text-xs text-white/60">
          {ratio.description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-white">
              {latestValue !== undefined ? formatValue(latestValue) : "N/A"}
            </span>
            <div className="flex items-center text-xs text-white/60">
              <Info className="mr-1 h-3 w-3" />
              {ratio.formula}
            </div>
          </div>

          <div className="h-56">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis
                    dataKey="date"
                    fontSize={11}
                    stroke="rgba(255,255,255,0.6)"
                    tickLine={false}
                  />
                  <YAxis
                    fontSize={11}
                    stroke="rgba(255,255,255,0.6)"
                    tickLine={false}
                    tickFormatter={(value) =>
                      ratio.isPercentage
                        ? `${(value * 100).toFixed(0)}%`
                        : ratio.isDays
                          ? `${value}d`
                          : value.toFixed(1)
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#4da3ff"
                    strokeWidth={2.5}
                    dot={{ fill: "#4da3ff", r: 4 }}
                    activeDot={{ r: 6, fill: "#4da3ff" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="text-white/60">No data available</div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const QUESTION_ID = "ratio-analysis-observations";
const QUESTION = "Document our observation and conclusions drawn based on the above trend";

export default function TrdPblRatioAnalysis({ onBack, setActiveSection }: TrdPblRatioAnalysisProps) {
  const handleBack = onBack || (() => {
    if (setActiveSection) {
      setActiveSection("trdpbl-substantive");
    }
  });

  const [companyName, setCompanyName] = useState<string>("");
  const [areaPrefix, setAreaPrefix] = useState<string>("TP");
  const [responses, setResponses] = useState<RatioResponse[]>([]);
  const [inferenceQuestion, setInferenceQuestion] = useState<RatioResponse>({
    id: "inference-conclusion",
    textResponse: null,
    documents: [],
  });
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `TRADE_PAYABLES_RATIO_ANALYSIS_${normalizedSection}`;
  };
  const [dataLoading, setDataLoading] = useState(true);
  const [chartData, setChartData] = useState<{
    creditorTurnover: { date: string; value: number }[];
    creditorDays: { date: string; value: number }[];
  }>({
    creditorTurnover: [],
    creditorDays: [],
  });

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn("Failed to load company name, using fallback");
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load area prefix from Sections.json
  useEffect(() => {
    const loadAreaPrefix = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const sectionsData = result.data;
            if (sectionsData.Execution && sectionsData.Execution["Trade Payables"]) {
              setAreaPrefix(sectionsData.Execution["Trade Payables"]);
            }
          }
        }
      } catch (error) {
        console.warn("Failed to load area prefix from Sections.json, using default TP:", error);
      }
    };
    loadAreaPrefix();
  }, []);

  // Helper function to parse date string (DD/MM/YY format) - same as Financial Ratios
  const parseDate = (dateStr: string): Date => {
    const [day, month, year] = dateStr.split('/');
    // Convert 2-digit year to 4-digit (assuming 20xx)
    const fullYear = parseInt(year) < 50 ? 2000 + parseInt(year) : 1900 + parseInt(year);
    return new Date(fullYear, parseInt(month) - 1, parseInt(day));
  };

  // Helper function to find latest dates from JSON keys - same as Financial Ratios
  const findLatestDates = (data: any, count: number = 4): string[] => {
    const dates = Object.keys(data).filter(key => {
      // Check if key matches date format DD/MM/YY
      return /^\d{2}\/\d{2}\/\d{2}$/.test(key);
    });
    
    if (dates.length === 0) return [];
    
    // Sort dates and return the latest N dates
    dates.sort((a, b) => {
      const dateA = parseDate(a);
      const dateB = parseDate(b);
      return dateB.getTime() - dateA.getTime();
    });
    
    return dates.slice(0, Math.min(count, dates.length));
  };

  // Load financial ratios data - same logic as Financial Ratios section
  useEffect(() => {
    const loadFinancialData = async () => {
      try {
        setDataLoading(true);
        console.log('Loading Financial Ratios data from SharePoint...');

        // Download FinData_Formulae.json from SharePoint - same as Financial Ratios
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("FinData_Formulae.json");
          
          if (result?.success && result.data) {
            const data = result.data;
            
            // Find latest dates (up to 4) - same as Financial Ratios
            const dates = findLatestDates(data, 4);
            if (dates.length === 0) {
              console.warn("No valid dates found in financial data");
              setDataLoading(false);
              return;
            }
            
            // Build chart data for all dates (reverse order so latest is on the right) - same as Financial Ratios
            const reversedDates = [...dates].reverse(); // Reverse to show oldest on left, latest on right
            
            const creditorTurnoverData = reversedDates.map(date => {
              const dateData = data[date] as any;
              const value = dateData?.formulae?.Creditor_turnover_ratio || 0;
              return {
                date,
                value,
              };
            });

            const creditorDaysData = reversedDates.map(date => {
              const dateData = data[date] as any;
              const value = dateData?.formulae?.Creditor_days || 0;
              return {
                date,
                value,
              };
            });

            setChartData({
              creditorTurnover: creditorTurnoverData,
              creditorDays: creditorDaysData,
            });
            
            console.log('Financial Ratios data loaded successfully for dates:', dates);
          } else {
            console.warn(result?.error || "Failed to download financial data");
          }
        } else {
            console.warn("SharePoint API not available");
        }
      } catch (error) {
        console.error('Failed to load Financial Ratios data:', error);
      } finally {
        setDataLoading(false);
      }
    };
    loadFinancialData();
  }, []);

  // Load saved responses
  useEffect(() => {
    const loadSavedResponses = async () => {
      if (!companyName || !areaPrefix) {
        setLoading(false);
        return;
      }

      try {
        const fileName = `Execution_${areaPrefix}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);

        if (result?.success && result?.data?.substantiveProcedures) {
          const savedData = result.data.substantiveProcedures["Ratio Analysis"];
          if (savedData && Array.isArray(savedData.responses)) {
            setResponses(savedData.responses.map((r: any, index: number) => ({
              id: r.id || `response-${index}`,
              textResponse: r.textResponse || null,
              documents: (r.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            })));
          } else if (savedData) {
            // Legacy format - single response
            setResponses([{
              id: "response-0",
              textResponse: savedData.textResponse || null,
              documents: (savedData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            }]);
          }
          
          // Load inference question
          const inferenceId = "TP-RATIO-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              id: "inference-conclusion",
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.error("Failed to load saved responses:", error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedResponses();
  }, [companyName, areaPrefix]);

  const handleAddResponse = () => {
    const newResponse: RatioResponse = {
      id: `response-${Date.now()}`,
      textResponse: null,
      documents: [],
    };
    setResponses([...responses, newResponse]);
  };

  const handleRemoveResponse = (id: string) => {
    setResponses(responses.filter(r => r.id !== id));
  };

  const handleTextResponseChange = (id: string, textResponse: { html: string; plainText: string } | null) => {
    setResponses(responses.map(r => 
      r.id === id ? { ...r, textResponse } : r
    ));
  };

  const handleDocumentsChange = (id: string, documents: Array<{ id: string; type: string; name: string; content?: string; url?: string }>) => {
    setResponses(responses.map(r => 
      r.id === id ? { ...r, documents } : r
    ));
  };

  const handleSave = useCallback(async () => {
    if (!companyName || !areaPrefix) {
      setSaveError("Missing required data (company name or area prefix)");
      setSaveStatus('error');
      return;
    }

    // Auto-save any pending textbox content before processing responses
    const documentManagers = document.querySelectorAll('[data-document-manager]');
    for (const manager of Array.from(documentManagers)) {
      const itemId = manager.getAttribute('data-item-id');
      if (itemId) {
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === 'function') {
          autoSaveMethod();
        }
      }
    }

    // Wait for auto-save to complete
    await new Promise(resolve => setTimeout(resolve, 1500));

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const fileName = `Execution_${areaPrefix}_Responses.json`;

      // Load existing file first
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save the responses
      const responsesToSave = responses.map(r => ({
        id: r.id,
        textResponse: r.textResponse || null,
        documents: (r.documents || []).map((d) => d.name || d),
      }));

      existingData.substantiveProcedures["Ratio Analysis"] = {
        responses: responsesToSave,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "TP-RATIO-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponse = inferenceQuestion.textResponse || null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponse = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponse,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      // Upload to SharePoint
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save responses:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [companyName, areaPrefix, responses, inferenceQuestion]);

  if (loading || dataLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-white/60" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Ratio Analysis - Substantive Procedures</h2>
          <p className="text-gray-400">Creditor Turnover Ratio and Creditor Days Analysis</p>
        </div>
        {handleBack && (
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <RatioCard
          ratio={{
            name: "Creditor Turnover Ratio",
            key: "Creditor_turnover_ratio",
            formula: "Purchases / Average Payables",
            description: "How efficiently payables are managed",
            isPercentage: false,
            isDays: false,
          }}
          chartData={chartData.creditorTurnover}
        />
        <RatioCard
          ratio={{
            name: "Creditor Days",
            key: "Creditor_days",
            formula: "365 / Creditor Turnover Ratio",
            description: "Average payment period",
            isPercentage: false,
            isDays: true,
          }}
          chartData={chartData.creditorDays}
        />
      </div>

      {/* Observations Section */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Observations and Conclusions</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Observations and Conclusions"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
              <Button
                onClick={handleAddResponse}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {responses.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              <p>No observations added yet. Click "Add" to add your first observation.</p>
            </div>
          ) : (
            responses.map((response, index) => (
              <Card key={response.id} className="border-white/10 bg-black/20">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-white">
                      {QUESTION} {responses.length > 1 ? `(${index + 1})` : ''}
                    </Label>
                    {responses.length > 1 && (
                      <Button
                        onClick={() => handleRemoveResponse(response.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <DocumentManager
                    sectionKey={`execution-substantive:Trade Payables`}
                    itemId={`${QUESTION_ID}-${response.id}`}
                    itemQuestion={QUESTION}
                    itemTextResponse={response.textResponse || null}
                    itemDocuments={response.documents || []}
                    onDocumentsChange={(docs) => handleDocumentsChange(response.id, docs)}
                    onTextResponseChange={(textResp) => handleTextResponseChange(response.id, textResp)}
                    disabled={false}
                  />
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey={`execution-substantive:Trade Payables`}
            itemId="TP-RATIO-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse || null}
            itemDocuments={inferenceQuestion.documents || []}
            onDocumentsChange={(docs) => setInferenceQuestion({ ...inferenceQuestion, documents: docs })}
            onTextResponseChange={(textResp) => setInferenceQuestion({ ...inferenceQuestion, textResponse: textResp })}
            disabled={false}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end items-center gap-4 pt-6">
        {saveStatus === 'saved' && (
          <Badge variant="outline" className="border-green-600 text-green-600">
            Saved successfully
          </Badge>
        )}
        {saveStatus === 'error' && saveError && (
          <Badge variant="destructive" className="text-red-600">
            Error: {saveError}
          </Badge>
        )}
        <Button
          onClick={handleSave}
          className="flex items-center gap-2"
          disabled={saving || !companyName || !areaPrefix}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Responses
            </>
          )}
        </Button>
      </div>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Observations and Conclusions")]: "Observations and Conclusions",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}

